import type { ReadonlyURLSearchParams } from "next/navigation";

import { notEmpty } from "shared/lib/utils";

import type { PlusDetailedListing } from "./types";

export const QueryParameterName = "plus-detail";
export const QuerySeparator = ":";

type EncodedPlusDetailedListingQuery = ReturnType<typeof encodePlusDetailedListingQuery>;

type KeysOfEncodedPlusDetailedListingQuery = Array<KeysOfUnion<EncodedPlusDetailedListingQuery>>;

/** ref: https://stackoverflow.com/a/49402091/5235017 */
type KeysOfUnion<T> = T extends T ? keyof T : never;

export function decodePlusDetailedListingQuery(
  searchParameters: ReadonlyURLSearchParams,
): PlusDetailedListing | undefined {
  const plusDetailQueryString = searchParameters.get(QueryParameterName) ?? "";
  const [type, entityUuid] = plusDetailQueryString.split(QuerySeparator).filter(notEmpty);

  switch (type) {
    case "author":
    case "topic":
    case "topic-without-related-contents":
      return { entityUuid, type };
    case "between-the-lines":
    case "china-future-tech-briefing":
    case "daily-pulse":
    case "fact-sheet":
    case "featured-opinion":
    case "interactives":
    case "open-questions-series":
    case "quick-digest":
      return { type };
    case "nodequeue": {
      const queueName = searchParameters.get("queueName") ?? "";
      const name = searchParameters.get("name") ?? "";
      const icon = searchParameters.get("icon") ?? "";
      const description = searchParameters.get("description") ?? "";
      if (!queueName) return undefined;
      return { description, icon, name, queueName, type: "nodequeue" };
    }
    case "series": {
      const seriesEntityId = entityUuid ?? searchParameters.get("seriesEntityId") ?? "";
      if (!seriesEntityId) return undefined;
      const name = searchParameters.get("name") ?? "";
      const icon = searchParameters.get("icon") ?? "";
      return { icon, name, seriesEntityId, type };
    }
  }
}

export function encodePlusDetailedListingQuery(plusDetailedListing: PlusDetailedListing) {
  const { type: _type, ...rest } = plusDetailedListing;
  switch (plusDetailedListing.type) {
    case "author":
    case "topic":
    case "topic-without-related-contents":
      return {
        [QueryParameterName]: [plusDetailedListing.type, plusDetailedListing.entityUuid].join(
          QuerySeparator,
        ),
        ...rest,
      };
    case "between-the-lines":
    case "china-future-tech-briefing":
    case "daily-pulse":
    case "fact-sheet":
    case "featured-opinion":
    case "interactives":
    case "open-questions-series":
    case "quick-digest":
      return { [QueryParameterName]: plusDetailedListing.type, ...rest };
    case "nodequeue":
      return {
        description: plusDetailedListing.description,
        icon: plusDetailedListing.icon,
        name: plusDetailedListing.name,
        [QueryParameterName]: plusDetailedListing.type,
        queueName: plusDetailedListing.queueName,
        ...rest,
      };
    case "series":
      return {
        icon: plusDetailedListing.icon,
        name: plusDetailedListing.name,
        [QueryParameterName]: [plusDetailedListing.type, plusDetailedListing.seriesEntityId].join(
          QuerySeparator,
        ),
        seriesEntityId: plusDetailedListing.seriesEntityId,
        ...rest,
      };
    default:
      return { [QueryParameterName]: "" };
  }
}

export const keysOfEncodedPlusDetailedListingQuery: KeysOfEncodedPlusDetailedListingQuery = [
  QueryParameterName,
  "entityUuid",
  "pgtype",
  "queueName",
  "name",
  "icon",
  "description",
  "seriesEntityId",
];
